import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import Card from 'app/frontend/base/components/Card'
import {
  NorthernSkyRaidToolsLink,
  TimelineRemindersLink,
} from 'app/frontend/base/components/AddonLinks'
import LinkButton from '@/components/ui/link-button'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import CreateNotePreview from '../../TeamEvent/components/CreateNotePreview'
import {
  getRaidlistParticipantCharacterId,
  clearRaidPlanCreateNoteState,
  loadRaidPlanCreateNoteState,
  parseRaidlistString,
  ParsedRaidlistParticipant,
  saveRaidPlanCreateNoteState,
} from './createNoteStorage'
import { EventPlanOverridesByPlanId } from '../../TeamEvent/components/eventActivityPlanOverrides'
import { Input } from '@/components/ui/input'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CircleHelp } from 'lucide-react'
import {
  getClass,
  getSpecialization,
} from 'app/frontend/base/services/characterValues'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import CharacterRole, {
  characterRoles,
} from 'app/frontend/types/custom/CharacterRole'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'

type RoleSummaryEntry = { name: string; className?: string }
type RoleSummary = Record<CharacterRole, RoleSummaryEntry[]> & {
  unknown: RoleSummaryEntry[]
}

type Props = {
  owner: RaidPlanOwner
  raidPlan: RaidPlan
  planListPathPrefix: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const CreateNoteDialog = ({
  owner,
  raidPlan,
  planListPathPrefix,
  open,
  onOpenChange,
}: Props) => {
  const { t } = useTranslation()
  const [state, setState] = useState<{
    raidlistInput: string
    parsedParticipants: ParsedRaidlistParticipant[] | null
    planOverrides: EventPlanOverridesByPlanId
  }>(() => {
    const stored = loadRaidPlanCreateNoteState(raidPlan.id)
    if (!stored) {
      return {
        raidlistInput: '',
        parsedParticipants: null,
        planOverrides: {},
      }
    }
    return {
      raidlistInput: stored.raidlistRaw,
      parsedParticipants: stored.parsedParticipants,
      planOverrides: stored.overrides ?? {},
    }
  })
  const [parseError, setParseError] = useState<string | null>(null)
  const [isUpdateSetupPopoverOpen, setIsUpdateSetupPopoverOpen] =
    useState(false)
  const [
    stickyActorCharacterIdsByActorUuid,
    setStickyActorCharacterIdsByActorUuid,
  ] = useState<Record<string, number>>({})
  const resolvedActorIdentityMapRef = useRef<Record<string, string>>({})
  const isUserScoped = owner.ownerType === 'user'
  const { copied, copyToClipboard } = useCopyToClipboard()
  const [resolvedNote, setResolvedNote] = useState('')

  const persistState = (next: typeof state) => {
    if (!next.parsedParticipants) return
    saveRaidPlanCreateNoteState(raidPlan.id, {
      raidlistRaw: next.raidlistInput,
      parsedParticipants: next.parsedParticipants,
      overrides: next.planOverrides,
    })
  }

  const setRaidlistInput = (value: string) => {
    setState((previous) => {
      const next = { ...previous, raidlistInput: value }
      persistState(next)
      return next
    })
  }

  const roleSummary = useMemo(() => {
    const empty: RoleSummary = {
      tank: [],
      heal: [],
      melee: [],
      ranged: [],
      unknown: [],
    }
    if (!state.parsedParticipants) return empty

    state.parsedParticipants.forEach((participant) => {
      const specialization =
        participant.specId === null
          ? undefined
          : getSpecialization(participant.specId.toString())
      const role = specialization?.default_role ?? 'unknown'
      const className = getClass(participant.classId.toString())
      const entry = {
        name: participant.name,
        className,
      }
      if (characterRoles.includes(role as CharacterRole)) {
        empty[role as CharacterRole].push(entry)
        return
      }
      empty.unknown.push(entry)
    })

    return empty
  }, [state.parsedParticipants])

  const roleSummaryKeys = useMemo(() => {
    const baseRoles = [...characterRoles] as CharacterRole[]
    return (
      roleSummary.unknown.length > 0 ? [...baseRoles, 'unknown'] : baseRoles
    ) as Array<CharacterRole | 'unknown'>
  }, [roleSummary])

  const handleSubmitRaidlist = () => {
    const parsed = parseRaidlistString(state.raidlistInput)
    if (!parsed) {
      setParseError(t('dashboard.team.plans.create-note.invalid-raidlist'))
      return
    }
    setParseError(null)
    setState((previous) => {
      const next = {
        ...previous,
        parsedParticipants: parsed,
        planOverrides: {},
      }
      persistState(next)
      return next
    })
    setStickyActorCharacterIdsByActorUuid({})
  }

  const handleUpdateParsedSetup = () => {
    const parsed = parseRaidlistString(state.raidlistInput)
    if (!parsed) {
      setParseError(t('dashboard.team.plans.create-note.invalid-raidlist'))
      return
    }
    setParseError(null)
    const participantIdentitySet = new Set(
      parsed.map((participant) => participant.identity),
    )
    const nextStickyActorCharacterIdsByActorUuid = Object.entries(
      resolvedActorIdentityMapRef.current,
    ).reduce<Record<string, number>>(
      (acc, [actorUuid, participantIdentity]) => {
        if (!participantIdentitySet.has(participantIdentity)) return acc
        acc[actorUuid] = getRaidlistParticipantCharacterId(participantIdentity)
        return acc
      },
      {},
    )
    setStickyActorCharacterIdsByActorUuid(
      nextStickyActorCharacterIdsByActorUuid,
    )
    setState((previous) => {
      const next = {
        ...previous,
        parsedParticipants: parsed,
      }
      persistState(next)
      return next
    })
    setIsUpdateSetupPopoverOpen(false)
  }

  const handleClearLocalState = () => {
    clearRaidPlanCreateNoteState(raidPlan.id)
    setState({
      raidlistInput: '',
      parsedParticipants: null,
      planOverrides: {},
    })
    setStickyActorCharacterIdsByActorUuid({})
    resolvedActorIdentityMapRef.current = {}
    setParseError(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[95vw] w-[1280px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {t('dashboard.team.plans.create-note.title')}
          </DialogTitle>
        </DialogHeader>

        <p className='max-w-[50rem]'>
          Your raid plan is a blueprint. Based on your setup of the day, a
          reminder note will be generated dynamically to use with{' '}
          <TimelineRemindersLink /> or <NorthernSkyRaidToolsLink />. You can
          make manual changes to the resolved note before exporting.
        </p>

        {state.parsedParticipants ? (
          <div className='space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <Card className='px-3 py-2 flex items-center gap-3'>
                {roleSummaryKeys.map((role, roleIndex) => {
                  const entries = roleSummary[role]
                  const label =
                    role === 'unknown'
                      ? t('shared.unknown')
                      : t(
                          `dashboard.team.plans.assignments.entities.roles.${role}`,
                        )
                  return (
                    <div key={role} className='flex items-center gap-3'>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className='flex items-center gap-1.5 cursor-default'>
                            {role === 'unknown' ? (
                              <CircleHelp className='h-4 w-4 text-muted-foreground' />
                            ) : (
                              <CharacterRoleIcon
                                role={role as CharacterRole}
                                size='small'
                              />
                            )}
                            <span className='text-xs font-semibold text-muted-foreground'>
                              {entries.length}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className='space-y-1'>
                            <p className='text-xs text-muted-foreground'>
                              {label}
                            </p>
                            {entries.length ? (
                              entries.map((entry: RoleSummaryEntry) => {
                                const classColor =
                                  entry.className &&
                                  classColours[
                                    entry.className as keyof typeof classColours
                                  ]
                                    ? classColours[
                                        entry.className as keyof typeof classColours
                                      ].text
                                    : 'text-foreground'
                                return (
                                  <p
                                    key={`${role}-${entry.name}`}
                                    className={classColor}
                                  >
                                    {entry.name}
                                  </p>
                                )
                              })
                            ) : (
                              <p className='text-muted-foreground'>
                                {t('shared.none')}
                              </p>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                      {roleIndex < roleSummaryKeys.length - 1 ? (
                        <div className='h-4 w-px bg-border' />
                      ) : null}
                    </div>
                  )
                })}
              </Card>
              <div className='flex items-center gap-2 flex-1 justify-end'>
                <Popover
                  open={isUpdateSetupPopoverOpen}
                  onOpenChange={setIsUpdateSetupPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button type='button' variant='outline'>
                      {t('dashboard.team.plans.create-note.update-setup')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-[min(17rem,90vw)] p-3'>
                    <div className='space-y-2'>
                      <p>Paste your exported raid setup here</p>
                      <Input
                        value={state.raidlistInput}
                        onChange={(event) =>
                          setRaidlistInput(event.target.value)
                        }
                        onFocus={(event) => event.currentTarget.select()}
                        placeholder={t(
                          'dashboard.team.plans.create-note.placeholder',
                        )}
                      />
                      <div className='flex justify-end'>
                        <Button type='button' onClick={handleUpdateParsedSetup}>
                          {t('dashboard.team.plans.create-note.submit')}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleClearLocalState}
                >
                  {t('dashboard.team.plans.create-note.clear')}
                </Button>
              </div>
            </div>
            {parseError ? (
              <p className='text-sm text-destructive-foreground'>
                {parseError}
              </p>
            ) : null}
            <CreateNotePreview
              owner={owner}
              raidPlan={raidPlan}
              planListPathPrefix={planListPathPrefix}
              parsedParticipants={state.parsedParticipants}
              planOverrides={state.planOverrides}
              stickyActorCharacterIdsByActorUuid={
                stickyActorCharacterIdsByActorUuid
              }
              onResolvedActorIdentityMapChange={(value) => {
                resolvedActorIdentityMapRef.current = value
              }}
              onResolvedNoteChange={setResolvedNote}
              onPlanOverridesChange={(nextPlanOverrides) => {
                setState((previous) => {
                  const next = {
                    ...previous,
                    planOverrides: nextPlanOverrides,
                  }
                  persistState(next)
                  return next
                })
              }}
            />
            <div className='flex justify-end'>
              <Button
                type='button'
                onClick={() => copyToClipboard(resolvedNote)}
                disabled={resolvedNote.trim().length === 0}
              >
                {copied ? 'Copied!' : 'Copy note'}
              </Button>
            </div>
          </div>
        ) : isUserScoped ? (
          <div className='space-y-3'>
            <p>
              You can import your setup using the{' '}
              <a
                href='https://www.curseforge.com/wow/addons/wowaudit-companion'
                target='_blank'
                rel='noopener noreferrer'
                className='text-primary hover:text-primary/90'
              >
                companion addon
              </a>
              .
            </p>
            <Input
              value={state.raidlistInput}
              onChange={(event) => setRaidlistInput(event.target.value)}
              placeholder={t('dashboard.team.plans.create-note.placeholder')}
            />
            {parseError ? (
              <p className='text-sm text-destructive-foreground'>
                {parseError}
              </p>
            ) : null}
            <div className='flex justify-end'>
              <Button type='button' onClick={handleSubmitRaidlist}>
                {t('dashboard.team.plans.create-note.submit')}
              </Button>
            </div>
          </div>
        ) : (
          <div className='grid gap-4 md:grid-cols-2'>
            <Card className='space-y-2'>
              <h4>{t('dashboard.team.plans.create-note.events-card.title')}</h4>
              <p>
                {t('dashboard.team.plans.create-note.events-card.description')}
              </p>
              <LinkButton to={`${planListPathPrefix}/events`} variant='default'>
                {t('dashboard.team.plans.create-note.events-card.action')}
              </LinkButton>
            </Card>
            <Card className='space-y-2'>
              <h4>{t('dashboard.team.plans.create-note.paste-card.title')}</h4>
              <p>
                If you are already in a group in-game, you can import your setup
                using the{' '}
                <a
                  href='https://www.curseforge.com/wow/addons/wowaudit-companion'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary hover:text-primary/90'
                >
                  companion addon
                </a>
                .
              </p>
              <Input
                value={state.raidlistInput}
                onChange={(event) => setRaidlistInput(event.target.value)}
                placeholder={t('dashboard.team.plans.create-note.placeholder')}
              />
              {parseError ? (
                <p className='text-sm text-destructive-foreground'>
                  {parseError}
                </p>
              ) : null}
              <div className='flex justify-end'>
                <Button type='button' onClick={handleSubmitRaidlist}>
                  {t('dashboard.team.plans.create-note.submit')}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CreateNoteDialog
